import { useEffect, useState, useRef } from "react";
import { supabase } from "./lib/supabaseClient";
import QRCodeStyling from "qr-code-styling";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useNavigate } from "react-router-dom";
import useFolders from "./hooks/useFolders";
import FolderManager from "./components/FolderManager";

const QRCard = ({ qr, onSelectQR, onEditQR, onDelete, onDuplicate, onMoveToFolder, folders }) => {
  const qrRef = useRef(null);
  const qrCode = useRef(null);
  const [showFolderMenu, setShowFolderMenu] = useState(false);

  useEffect(() => {
    if (!qr) return;
    qrCode.current = new QRCodeStyling({
      width: 160, height: 160,
      data: (qr.qr_mode === "leads" || qr.qr_mode === "feedback")
        ? `https://qr.digitoly.com/go/${qr.code}`
        : qr.qr_mode === "direct"
        ? (qr.destination_url || "https://www.digitoly.com")
        : `https://qr.digitoly.com/go/${qr.code}`,
      margin: 5,
      dotsOptions: { color: "#ffffff", type: "extra-rounded" },
      backgroundOptions: { color: "transparent" },
      imageOptions: { margin: 5, imageSize: 0.4 },
      image: qr.image_data || qr.logo_url || ""
    });
    if (qrRef.current) {
      qrRef.current.innerHTML = "";
      qrCode.current.append(qrRef.current);
    }
  }, [qr]);

  if (!qr) return null;

  const isExpired = qr.expires_at && new Date(qr.expires_at) < new Date();
  const isMaxed = qr.max_scans && qr.total_scans >= qr.max_scans;
  const isInactive = !qr.is_active || isExpired || isMaxed;

  return (
    <div className={`group relative bg-[#0f172a]/40 backdrop-blur-xl border rounded-[40px] p-6 transition-all duration-500 h-full flex flex-col ${isInactive ? "border-red-500/20 opacity-60" : "border-white/5 hover:border-red-500/30 hover:shadow-2xl hover:shadow-red-500/10"}`}>

      {/* Status badge */}
      {isInactive && (
        <div className="absolute top-6 left-1/2 -translate-x-1/2 z-30 bg-red-500/20 border border-red-500/30 px-3 py-1 rounded-full">
          <span className="text-[9px] font-black text-red-400 uppercase">{isExpired ? "Expired" : isMaxed ? "Max Scans Reached" : "Inactive"}</span>
        </div>
      )}

      {/* Scan count */}
      <div className="absolute top-6 right-6 z-20 bg-white/5 backdrop-blur-md border border-white/10 px-3 py-1.5 rounded-2xl flex items-center gap-2">
        <div className={`w-1.5 h-1.5 rounded-full ${isInactive ? "bg-gray-500" : "bg-red-500 animate-pulse"}`}></div>
        <span className="text-[10px] font-black text-white uppercase tracking-widest">{qr.total_scans || 0} Scans</span>
      </div>

      {/* Delete */}
      <button
        onClick={() => onDelete(qr.id)}
        className="absolute top-6 left-6 z-20 bg-red-500/10 hover:bg-red-500 text-red-500 hover:text-white p-2 rounded-lg transition-all opacity-0 group-hover:opacity-100"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
          <path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
        </svg>
      </button>

      {/* QR */}
      <div className="flex flex-col items-center flex-grow mt-4">
        <div className="p-2 rounded-3xl mb-4"><div ref={qrRef} /></div>
        <div className="text-center w-full px-2">
          <p className="text-gray-500 text-[10px] font-bold uppercase mb-1 tracking-widest">
            {new Date(qr.created_at).toLocaleDateString()}
          </p>
          <p className="text-white text-xs truncate mb-2">{qr.name || qr.destination_url}</p>
          <div className="flex items-center justify-center gap-2 flex-wrap">
            <span className={`text-[9px] px-3 py-1 rounded-full font-black uppercase ${qr.is_dynamic ? "bg-red-500/10 text-red-400" : "bg-slate-700/50 text-slate-400"}`}>
              {qr.is_dynamic ? "Dynamic ⚡" : "Static"}
            </span>
            {qr.qr_mode && (
              <span className="text-[9px] px-3 py-1 rounded-full font-black uppercase bg-blue-500/10 text-blue-400">
                {qr.qr_mode}
              </span>
            )}
            {qr.pin_code && <span className="text-[9px] px-2 py-1 rounded-full bg-yellow-500/10 text-yellow-400">🔒 PIN</span>}
            {qr.ab_testing && <span className="text-[9px] px-2 py-1 rounded-full bg-purple-500/10 text-purple-400">A/B</span>}
          </div>
          {qr.expires_at && !isExpired && (
            <p className="text-[9px] text-gray-600 mt-1">Expires {new Date(qr.expires_at).toLocaleDateString()}</p>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="grid grid-cols-2 gap-2 mt-6">
        <button onClick={() => onSelectQR(qr)} className="bg-red-600 text-white text-[10px] font-black py-3 rounded-xl">Analytics</button>
        <button onClick={() => onEditQR(qr)} className="bg-white/5 text-white text-[10px] font-black py-3 rounded-xl">Edit</button>
        <button onClick={() => onDuplicate(qr)} className="bg-white/5 text-white text-[10px] font-black py-2.5 rounded-xl col-span-1">Duplicate</button>
        <div className="relative">
          <button
            onClick={() => setShowFolderMenu(!showFolderMenu)}
            className="w-full bg-white/5 text-white text-[10px] font-black py-2.5 rounded-xl"
          >
            📁 Move
          </button>
          {showFolderMenu && (
            <div className="absolute bottom-full mb-2 left-0 right-0 bg-slate-900 border border-white/10 rounded-xl p-2 z-50 space-y-1">
              <button
                onClick={() => { onMoveToFolder(qr.id, null); setShowFolderMenu(false); }}
                className="w-full text-left px-3 py-2 text-[10px] font-black text-gray-400 hover:bg-white/10 rounded-lg"
              >
                No Folder
              </button>
              {folders.map(f => (
                <button
                  key={f.id}
                  onClick={() => { onMoveToFolder(qr.id, f.id); setShowFolderMenu(false); }}
                  className="w-full text-left px-3 py-2 text-[10px] font-black text-white hover:bg-white/10 rounded-lg flex items-center gap-2"
                >
                  <span style={{ color: f.color }}>●</span> {f.name}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default function MyQRCodes({ user }) {
  const navigate = useNavigate();
  const [qrs, setQrs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFolder, setActiveFolder] = useState(null);
  const [search, setSearch] = useState("");
  const { folders, createFolder, deleteFolder } = useFolders(user?.id);

  const downloadMonthlyReport = () => {
    if (!qrs.length) return alert("No campaigns found.");
    const doc = new jsPDF();
    const monthYear = new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' }).toUpperCase();
    doc.text("DIGITAL ASSET PERFORMANCE REPORT", 15, 20);
    doc.text(monthYear, 15, 28);
    doc.text(`Generated for: ${user?.email}`, 15, 36);
    autoTable(doc, {
      startY: 45,
      head: [['Campaign', 'Mode', 'Type', 'Scans', 'Date']],
      body: qrs.map(qr => [qr.name || "Untitled", qr.qr_mode || "direct", qr.is_dynamic ? "DYNAMIC" : "STATIC", qr.total_scans || 0, new Date(qr.created_at).toLocaleDateString()])
    });
    doc.save(`Report_${monthYear}.pdf`);
  };

  const fetchQRs = async () => {
    if (!user?.id) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("qr_codes").select("*").eq("user_id", user.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      setQrs((data || []).map(qr => ({ ...qr, scans: qr.total_scans || 0 })));
    } catch (err) {
      console.error("fetchQRs error:", err.message);
      setQrs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchQRs(); }, [user?.id]);

  const handleDuplicate = async (qr) => {
    const newCode = Math.random().toString(36).substring(2, 8);
    const { id, created_at, total_scans, ...rest } = qr;
    const { error } = await supabase.from("qr_codes").insert([{
      ...rest,
      code: newCode,
      name: `${qr.name || "QR"} (Copy)`,
      total_scans: 0,
    }]);
    if (!error) { fetchQRs(); }
  };

  const handleMoveToFolder = async (qrId, folderId) => {
    await supabase.from("qr_codes").update({ folder_id: folderId }).eq("id", qrId);
    setQrs(prev => prev.map(q => q.id === qrId ? { ...q, folder_id: folderId } : q));
  };

  const filteredQRs = qrs
    .filter(qr => activeFolder ? qr.folder_id === activeFolder : true)
    .filter(qr => search ? (qr.name || "").toLowerCase().includes(search.toLowerCase()) : true);

  if (loading) return (
    <div className="flex items-center justify-center p-20">
      <div className="w-8 h-8 border-4 border-red-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center border-b border-white/5 pb-6">
        <h1 className="text-3xl font-black text-white">My QR Codes</h1>
        <div className="flex gap-3">
          <button onClick={downloadMonthlyReport} className="bg-white/5 text-white px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-red-500 transition-all">
            Generate Report
          </button>
          <button onClick={() => navigate("/dashboard/create")} className="bg-red-600 text-white px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-red-500 transition-all">
            + Create QR
          </button>
        </div>
      </div>

      {/* Search */}
      <input
        type="text"
        placeholder="Search QR codes..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-white text-sm outline-none focus:border-red-500 transition-all"
      />

      {/* Folders */}
      <FolderManager
        folders={folders}
        onCreateFolder={createFolder}
        onDeleteFolder={deleteFolder}
        activeFolder={activeFolder}
        onSelectFolder={setActiveFolder}
      />

      {/* Stats bar */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: "Total QRs", value: qrs.length },
          { label: "Total Scans", value: qrs.reduce((a, q) => a + (q.total_scans || 0), 0) },
          { label: "Active", value: qrs.filter(q => q.is_active !== false).length },
        ].map(s => (
          <div key={s.label} className="bg-white/5 border border-white/10 rounded-2xl p-4 text-center">
            <p className="text-[9px] font-black uppercase text-gray-500 tracking-widest">{s.label}</p>
            <p className="text-2xl font-black text-white mt-1">{s.value.toLocaleString()}</p>
          </div>
        ))}
      </div>

      {/* Grid */}
      {filteredQRs.length === 0 ? (
        <div className="border border-white/10 rounded-[32px] p-20 text-center">
          <p className="text-slate-500 text-sm font-bold uppercase tracking-widest">
            {search ? "No QR codes match your search." : activeFolder ? "No QR codes in this folder." : "No QR codes yet."}
          </p>
          {!search && !activeFolder && (
            <button onClick={() => navigate("/dashboard/create")} className="mt-6 bg-red-600 text-white px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest">
              Create Your First QR
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredQRs.map(qr => (
            <QRCard
              key={qr.id}
              qr={qr}
              folders={folders}
              onSelectQR={qr => navigate(`/dashboard/qr/${qr.id}`)}
              onEditQR={qr => navigate(`/dashboard/edit/${qr.id}`)}
              onDuplicate={handleDuplicate}
              onMoveToFolder={handleMoveToFolder}
              onDelete={async id => {
                if (window.confirm("Delete this QR code?")) {
                  await supabase.from("qr_codes").delete().eq("id", id);
                  setQrs(prev => prev.filter(q => q.id !== id));
                }
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}